import { Component, OnInit, ViewChildren, QueryList, OnDestroy, ViewChild } from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/zip';
import 'rxjs/add/operator/finally';
import { TranslateService } from '@ngx-translate/core';

import { ProcessService } from 'app/core/process/process.service';
import * as Models from 'app/core/models';
import { AuthenticationService } from 'app/core/authentication/authentication.service';
import { ModalService } from 'app/modal/modal.service';
import { LookupService } from 'app/core/lookup/lookup.service';
import { ToastService, SBItemComponent } from 'libs/mdbootstrap/typescripts/pro/index';
import { CompleterComponent } from 'libs/mdbootstrap/typescripts/pro/autocomplete';
import { UserService } from 'app/core/user/user.service';
import { I18nService } from 'app/core/i18n.service';
import { PageTitleService } from '../shared/helpers/pageTitle.service';

@Component({
  selector: 'app-execute-process',
  templateUrl: './executeProcess.component.html',
  styleUrls: ['./executeProcess.component.scss'],
})
export class ExecuteProcessComponent implements OnInit, OnDestroy {

  @ViewChildren(CompleterComponent) completers: QueryList<CompleterComponent>;
  @ViewChildren(SBItemComponent) accordionItems: QueryList<SBItemComponent>;

  private error: any = null;
  private processId: number = null;
  private executableProcess: Models.ExecutableProcess = null;
  private process: Models.Process = null;
  private executionDetails: Models.ExecutionDetails;
  private currentStageIndex: number;
  private currentStepIndex: number;
  private stages: Models.ProcessStage[];
  private maxStageIndex = 0;
  private maxStepIndex = 0;

  private isStepLoading = false;
  private isLoading = true;
  private showContent = false;
  private showError = false;
  private stepTypes: Models.StepType[] = [];
  private processLanguage: Models.Languages = Models.Languages.enUS;
  private generalErrorMsg: string;


  constructor(private formBuilder: FormBuilder,
    private processService: ProcessService,
    private lookupService: LookupService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private actRoute: ActivatedRoute,
    private translateService: TranslateService,
    private modalService: ModalService,
    private toastrService: ToastService,
    private userService: UserService,
    private domSanitizer: DomSanitizer,
    private pageTitleService: PageTitleService,
    private i18nService: I18nService) {
  }

  ngOnInit() {
    this.isLoading = true;
    this.processId = +this.actRoute.snapshot.paramMap.get('id');
    Observable
      .zip(
        this.processService.getExecutableProcess(this.processId),
        this.lookupService.getStepTypes(Models.Languages.enUS)
      )
      .finally(() => {
        this.isLoading = false;
      })
      .subscribe(([executableProcess, stepTypes]) => {
        this.processData(executableProcess);
        this.stepTypes = stepTypes;
        this.error = null;
        this.showError = false;
        this.showContent = true;
      }, error => {
        this.error = error;
        this.showContent = false;
        this.showError = true;
      });

    this.translateService.get('PROCESS.EXECUTE.GENERAL_ERROR').subscribe((translatedMsg) => {
      this.generalErrorMsg = translatedMsg;
    });
  }

  ngOnDestroy(): void {
    this.i18nService.language = Models.EnumHelper.GetDescription(Models.Languages, Models.Languages.enUS);
  }

  get totalSteps(): number {
    let totalSteps = 0;

    if (this.stages != null && this.stages.length > 0) {
      this.stages.forEach(x => {
        totalSteps += x.steps.length;
      });
    }
    return totalSteps;
  }

  get stepsDone(): number {
    let stepsDoneCount = 0;
    if (this.stages != null && this.stages.length > 0) {
      for (let i = 0; i < this.maxStageIndex; i++) {
        stepsDoneCount += this.stages[i].steps.length;
      }

      stepsDoneCount += this.maxStepIndex;
    }
    return stepsDoneCount;
  }

  get currentStage(): Models.ProcessStage {
    return this.stages != null && this.stages.length >= this.currentStageIndex ? this.stages[this.currentStageIndex] : null;
  }

  get currentStep(): Models.ProcessStep {
    return this.currentStage != null ? this.currentStage.steps[this.currentStepIndex] : null;
  }

  get isAuthenticated(): boolean {
    return this.authenticationService.isAuthenticated();
  }

  get errorMessage(): string {
    if (this.error) {
      const errorBody = this.error._body ? JSON.parse(this.error._body) : null;
      if (errorBody && errorBody.error) {
        return errorBody.error;
      }
    }
    return this.generalErrorMsg;
  }

  get isLastStep(): boolean {
    return (this.currentStageIndex === (this.stages.length - 1) && this.currentStepIndex === (this.currentStage.steps.length - 1));
  }

  isStageDone(stageIndex: number): boolean {
    return stageIndex < this.maxStageIndex;
  }

  isStepDone(stageIndex: number, stepIndex: number): boolean {
    if (stageIndex < this.maxStageIndex) {
      return true;
    } else if (stageIndex > this.maxStageIndex) {
      return false;
    } else {
      return stepIndex < this.maxStepIndex;
    }
  }

  showStageInContentTable(stageIndex: number): boolean {
    return stageIndex <= this.maxStageIndex;
  }

  showStepInContentTable(stageIndex: number, stepIndex: number): boolean {
    if (stageIndex < this.maxStageIndex) {
      return true;
    } else if (stageIndex > this.maxStageIndex) {
      return false;
    } else {
      return stepIndex <= this.maxStepIndex;
    }
  }

  creatorsHelp() {
    const data: Models.ModalContext = {
      modalType: Models.ModalType.CONTACT_CREATOR,
      modalAction: Models.ModalAction.OPEN,
      parameters: [
        this.executionDetails.processExecutionId,
        this.currentStage.order,
        this.currentStage.title,
        this.currentStep.order,
        this.currentStep.title]
    };
    this.modalService.command(data);
  }

  nextStep() {
    if (this.isLastStep) {
      this.completeExecution();
    } else {
      let lastStageIndex = this.currentStageIndex;
      if (this.currentStage.steps.length > this.currentStepIndex + 1) {
        this.currentStepIndex++;
      } else if (this.stages.length > this.currentStageIndex + 1) {
        this.currentStageIndex++;
        this.currentStepIndex = 0;
      }

      if ((this.currentStageIndex < this.maxStageIndex) ||
        (this.currentStageIndex === this.maxStageIndex && this.currentStepIndex <= this.maxStepIndex)) {

        this.resetExecutionData(this.currentStageIndex, this.currentStepIndex);
      }

      this.maxStageIndex = this.currentStageIndex;
      this.maxStepIndex = this.currentStepIndex;

      if (lastStageIndex != this.currentStageIndex) {
        this.openContentAccordionSection(this.currentStageIndex);
      }

      this.saveExecutionStep();
    }
  }

  previousStep() {
    let lastStageIndex = this.currentStageIndex;
    
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
    } else if (this.currentStageIndex > 0) {
      this.currentStageIndex--;
      this.currentStepIndex = this.currentStage.steps.length - 1;
    }

    this.resetExecutionData(this.currentStageIndex, this.currentStepIndex + 1);

    this.maxStageIndex = this.currentStageIndex;
    this.maxStepIndex = this.currentStepIndex;

    
    if (lastStageIndex != this.currentStageIndex) {
      this.openContentAccordionSection(this.currentStageIndex);
    }

    this.saveExecutionStep();
  }

  setStep(stageIndex: number, stepIndex: number): void {
    this.currentStageIndex = stageIndex;
    this.currentStepIndex = stepIndex;
  }

  getStepTypeImage(stepTypeId: number): string {
    if (stepTypeId) {
      return this.stepTypes.find(x => x.stepTypeId === stepTypeId).imageUrl;
    } else {
      return 'https://proactiviewstaging.blob.core.windows.net/step-type-icons/default.png';
    }
  }

  private processData(executableProcess: Models.ExecutableProcess): void {
    this.executableProcess = executableProcess;
    this.process = this.executableProcess.processDetails;
    this.pageTitleService.setTitle(this.actRoute.data['value'].titleKey, this.process.title);
    this.executionDetails = this.executableProcess.executionDetails;

    this.processLanguage = !this.process.isRTL ? Models.Languages.enUS : Models.Languages.heIL;
    this.i18nService.language = Models.EnumHelper.GetDescription(Models.Languages, this.processLanguage);

    this.processMedia();
    this.processStagesAndSteps();
    this.processExecutionData();
    this.getCurrentStep();
  }

  private processMedia(): void {
    this.process.stages.forEach(stage => {
      stage.steps.forEach(step => {
        step.medias.forEach(media => {
          if (media.mediaType === 2) {
            const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
            const match = media.mediaUrl.match(regExp);
            media.videoId = (match && match[7].length === 11) ? match[7] : null;

            if (media.videoId) {
              const mediaUrl = this.buildMediaUrl(media);
              media.videoIframeUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(mediaUrl);
            }
          }
        });
        step.showTasks = !!(step.tasks && step.tasks.length);
      });
    });
  }

  private buildMediaUrl(media: any): string {
    const domain = 'https://www.youtube.com/embed/' + media.videoId + '?';
    const endParam = media.endTime ? 'end=' + media.endTime : '';
    const startParam = '&start=' + (media.startTime ? media.startTime : 0);

    return `${domain}${endParam}${startParam}&controls=0&loop=0&modestbranding=1&rel=0&showinfo=0&iv_load_policy=3`;
  }

  private processStagesAndSteps(): void {
    const stages: Models.ProcessStage[] = this.process.stages.sort((x1, x2) => x1.order - x2.order);
    stages.forEach(x => {
      x.steps = x.steps.sort((y1, y2) => y1.order - y2.order);
    });
    this.stages = stages;
  }

  private processExecutionData(): void {
    if (this.executionDetails.stages == null) {
      this.executionDetails.stages = [];
    }
    if (this.executionDetails.stages.length !== this.process.stages.length) {
      this.executionDetails.stages = [];

      this.process.stages.forEach(stage => {
        const stg: Models.ExecutionStage = { steps: [] };

        stage.steps.forEach(step => {
          const stp: Models.ExecutionStep = { tasks: [] };

          step.tasks.forEach(task => {
            stp.tasks.push(false);
          });

          stg.steps.push(stp);
        });

        this.executionDetails.stages.push(stg);
      });
    }
  }

  private getCurrentStep(): void {
    this.currentStageIndex = this.executionDetails.currentStage ? this.executionDetails.currentStage : 0;
    this.currentStepIndex = this.executionDetails.currentStep ? this.executionDetails.currentStep : 0;
    this.maxStageIndex = this.currentStageIndex;
    this.maxStepIndex = this.currentStepIndex;
  }

  private saveExecutionStep(): void {
    this.isStepLoading = true;
    const data: Models.SaveProcessExecutionModel = {
      processExecutionId: this.executionDetails.processExecutionId,
      currentStage: this.currentStageIndex,
      currentStep: this.currentStepIndex,
      stages: this.executionDetails.stages
    };
    this.processService.saveExecution(data)
      .finally(() => {
        this.isStepLoading = false;
      })
      .subscribe(returnData => {
        this.error = undefined;
      }, error => {
        this.error = error;
      });
  }

  private completeExecution(): void {
    this.processService.completeExecution(this.executionDetails.processExecutionId)
      .finally(() => {
        this.isStepLoading = false;

        if (!this.error) {
          const data: Models.ModalContext = {
            modalType: Models.ModalType.PROCESS_COMPLETE,
            modalAction: Models.ModalAction.OPEN,
            parameters: [
              Models.Process_Complete_Mode.EXECUTION,
              this.process.title,
              this.process.processId,
              this.process.coverSmallUrl,
              !this.process.canEdit,
              this.process.hasLiked,
              this.process.isRTL
            ]
          };
          this.modalService.command(data);
        }
      })
      .subscribe(result => {
        this.error = undefined;
      }, error => {
        this.error = error;
        this.toastrService.error(this.errorMessage);
      });
  }

  private resetExecutionData(startStageIndex: number = 0, startStepIndex: number = 0, ) {

    for (let x = startStageIndex; x < this.executionDetails.stages.length; x++) {

      let y: number = x === startStageIndex ? startStepIndex : 0;

      for (; y < this.executionDetails.stages[x].steps.length; y++) {

        let z = 0;

        for (z = 0; z < this.executionDetails.stages[x].steps[y].tasks.length; z++) {
          this.executionDetails.stages[x].steps[y].tasks[z] = false;
        }
      }
    }
  }

  private openContentAccordionSection(stageIndex: number) {
    let accordionItem = this.accordionItems.toArray()[stageIndex];
    if (accordionItem) {
      accordionItem.toggle(false);
    }
  }

}



// WEBPACK FOOTER //
// C:/ProactiView/ProactiView-Client/src/app/executeProcess/executeProcess.component.ts